import "../scss/landing_page.scss";

import * as basicLightbox from "basiclightbox";

import Pristine from "pristinejs";

import { easepick } from "@easepick/core";
import { AmpPlugin } from "@easepick/amp-plugin";
import { LockPlugin } from "@easepick/lock-plugin";

/**
 * Main JavaScript file for the front end part - the landing page.
 *
 * @package RootPixel
 * @subpackage JetskiSafari
 * @since JetskiSafari 1.0.0
 */

/**
 * Updates the fixed menu button's color
 */
function updateFixedButtonColor() {
    const sections = document.querySelectorAll("section");
    const button = document.getElementById("floating-menu-button");
    const buttonText = button.querySelector(".floating-menu-button__text");
    const decoLines = button.querySelectorAll(
        ".floating-menu-button__burger span"
    );
    let fixedButtonColor;

    sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (
            rect.top <= window.innerHeight / 2 &&
            rect.bottom >= window.innerHeight / 2
        ) {
            fixedButtonColor = section.dataset.fixedButtonColor;
        }
    });

    if (fixedButtonColor) {
        buttonText.style.color = `var(--rpd-color-frontend-${fixedButtonColor})`;
        decoLines.forEach((decoLine) => {
            decoLine.style.backgroundColor = `var(--rpd-color-frontend-${fixedButtonColor})`;
        });
    }
}

/**
 * Display / hide the fixed menu button on the top
 */
function updateFixedButtonVisibility() {
    const button = document.getElementById("floating-menu-button");
    const scrollPosition = window.scrollY;
    const halfViewportHeight = window.innerHeight / 2;

    if (scrollPosition >= halfViewportHeight) {
        button.style.display = "flex";
    } else {
        button.style.display = "none";
    }
}

/**
 * Lightbox for the gallery images
 */
function galleryLightbox() {
    const images = document.querySelectorAll(".masonry-gallery__image img");

    images.forEach((image) => {
        image.addEventListener("click", function () {
            const originalSrc = this.getAttribute("data-original");
            const description = this.getAttribute("alt");

            // Create a close button
            const closeButton = document.createElement("button");
            closeButton.textContent = "×";
            closeButton.classList.add("close-button");

            // Create the lightbox with the image and close button
            const lightbox = basicLightbox.create(
                `
                <div class="close-button-container">
                    <button type="button" aria-label="Close" class="close-button">×</button>
                </div>
                <div class="lightbox-content">
                    <img src="${originalSrc}" alt="${description}" />
                </div>
            `,
                {
                    onShow: (instance) => {
                        instance.element().querySelector("button").onclick =
                            instance.close;
                    },
                }
            );
            lightbox.show();
        });
    });
}

/**
 * Handle the FAQ item opening / closing
 */
function toggleFaqItems(faqItems, activeItem) {
    const isActive = activeItem.classList.contains("active");

    faqItems.forEach(function (item) {
        item.classList.remove("active");
        item.setAttribute("aria-expanded", "false");
    });

    if (!isActive) {
        activeItem.classList.add("active");
        activeItem.setAttribute("aria-expanded", "true");
    }
}

/**
 * Initialize the FAQ functionality for each FAQ columns (.js-faq-column)
 */
function initializeFaqColumns() {
    const faqColumns = document.querySelectorAll(".js-faq-column");

    faqColumns.forEach(function (column) {
        const faqItems = column.querySelectorAll(".faq-item");

        faqItems.forEach(function (faqItem) {
            const header = faqItem.querySelector(".faq-item__header");

            header.addEventListener("click", function () {
                toggleFaqItems(faqItems, faqItem);
            });
        });
    });
}

// Variables used by open/close menu
const floatingMenuButton = document.getElementById("floating-menu-button");
const headerNavigationButton = document.getElementById("header-navigation-btn");
const closeMenuButton = document.getElementById("close-menu-button");
let menu; // Create menu variable, define value in DOMContentLoaded
const header = document.querySelector(".main-header");

/**
 * Open Navigation
 */
function openMenu(event) {
    event.preventDefault();
    event.stopPropagation();
    menu.classList.add("main-navigation--open");
    header.style.zIndex = 0;

    floatingMenuButton.setAttribute("aria-expanded", "true");
    headerNavigationButton.setAttribute("aria-expanded", "true");
    closeMenuButton.setAttribute("aria-expanded", "true");

    // Add event listener to close the menu when clicking outside of it
    document.addEventListener("click", closeMenuOnClickOutside);
}

/**
 * Close Navigation
 */
function closeMenu(event) {
    // Check if event parameter is defined (when close button is clicked),
    // otherwise have to let flowing down the event
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }

    menu.classList.remove("main-navigation--open");
    header.style.zIndex = 1;

    floatingMenuButton.setAttribute("aria-expanded", "false");
    headerNavigationButton.setAttribute("aria-expanded", "false");
    closeMenuButton.setAttribute("aria-expanded", "false");

    // Remove the event listener for clicking outside of the menu
    document.removeEventListener("click", closeMenuOnClickOutside);
}

/**
 * Close navigation, when the visitor clicks outside of menu
 */
function closeMenuOnClickOutside(event) {
    if (!menu.contains(event.target)) {
        closeMenu(event);
    }
}

/**
 * Smooth Scroll to Section
 */
function smoothScrollToSection(event) {
    event.preventDefault(); // Prevent default behavior of the link
    const targetId = this.getAttribute("href"); // Get the target section's ID
    const targetSection = document.querySelector(targetId); // Find the target section
    if (targetSection) {
        targetSection.scrollIntoView({ behavior: "smooth" }); // Scroll to the target section smoothly
        closeMenu(); // Close the menu after scrolling
    }
}

/**
 * Form submit handler
 */
function submitForm(formId) {
    const form = document.getElementById(formId);
    const formData = new FormData(form);
    const loader = form.querySelector(".form-loader");
    const responseElement = form.querySelector(".response");

    // Remove error class, when there is any, and remove text
    responseElement.classList.remove("response--error");
    responseElement.textContent = "";

    // Show loading image
    loader.style.visibility = "visible";

    /* for (const [key, value] of formData.entries()) {
        console.log(key, value);
    } */

    fetch("/", {
        method: "POST",
        body: formData,
        headers: {
            "X-Requested-With": "XMLHttpRequest",
        },
    })
        .then((response) => {
            if (response.status === 422) {
                // Validation errors, parse response JSON and display error messages
                return response.json().then((errors) => {
                    throw new Error(JSON.stringify(errors));
                });
            }
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            return response.json();
        })
        .then((data) => {
            // Hide loading image
            loader.style.visibility = "hidden";

            // Display the success text
            responseElement.classList.add("response--success");
            responseElement.textContent =
                "Thank you for reaching out! We have received your message and will be in touch with you shortly.";

            // Display success message
            //console.log(data.message);

            // Reset the form
            form.reset();

            // Reset all styles after 5sec
            setTimeout(() => {
                // Reset response element
                responseElement.textContent = "";
                responseElement.classList.remove("response--success");

                // Remove form-container--opened class from parent element of actual form
                form.closest(".form-container").classList.remove(
                    "form-container--opened"
                );
            }, 5000);
        })
        .catch((error) => {
            // Hide loading image
            loader.style.visibility = "hidden";

            // Display error message
            //alert('Error: ' + error.message);
            responseElement.classList.add("response--error");
            responseElement.textContent =
                "Sorry, an error occurred while processing your request. Please try again later or refresh the page.";
        });
}

/**
 * Check if all required fields are filled out
 */
function checkRequiredFields(form) {
    const requiredFields = form.querySelectorAll("[required]");
    let allFieldsFilled = true;
    requiredFields.forEach((field) => {
        if (!field.value.trim()) {
            allFieldsFilled = false;
        }
    });
    return (
        allFieldsFilled &&
        form.querySelector('[name="gdpr_acceptance"]').checked
    );
}

/**
 * Enable or disable submit button based on required fields
 */
function updateSubmitButton(form, submitButton) {
    submitButton.disabled = !checkRequiredFields(form);
}

document.addEventListener("DOMContentLoaded", () => {
    // Add the element to the variable here (because early access error)
    menu = document.querySelector(".main-navigation");

    // Handle the open / close menu events
    floatingMenuButton.addEventListener("click", openMenu);
    headerNavigationButton.addEventListener("click", openMenu);
    closeMenuButton.addEventListener("click", closeMenu);

    // Get all menu links
    const menuLinks = document.querySelectorAll(".menu__item");

    // Attach smooth scroll event listener to each menu link
    menuLinks.forEach((link) => {
        link.addEventListener("click", smoothScrollToSection);
    });

    // Call the color changer function for fixed menu button
    updateFixedButtonColor();
    window.addEventListener("scroll", updateFixedButtonColor);
    window.addEventListener("resize", updateFixedButtonColor);

    // Show / hide the fixed menu button on the top
    updateFixedButtonVisibility();
    window.addEventListener("scroll", updateFixedButtonVisibility);
    window.addEventListener("resize", updateFixedButtonVisibility);

    // Call the lightbox for gallery images
    galleryLightbox();

    // Handle FAQ
    initializeFaqColumns();

    const jssDatepicker = new easepick.create({
        element: "#jss_datepicker",
        css: ["/static_resources/css/custom_datepicker.css"],
        zIndex: 10,
        format: "DD.MM.YYYY",
        grid: 1,
        calendars: 1,
        inline: false,
        plugins: [AmpPlugin, LockPlugin],
        AmpPlugin: {
            resetButton: true,
        },
        LockPlugin: {
            minDate: new Date(),
        },
    });

    const jsrDatepicker = new easepick.create({
        element: "#jsr_datepicker",
        css: ["/static_resources/css/custom_datepicker.css"],
        zIndex: 10,
        format: "DD.MM.YYYY",
        grid: 1,
        calendars: 1,
        inline: false,
        plugins: [AmpPlugin, LockPlugin],
        AmpPlugin: {
            resetButton: true,
        },
        LockPlugin: {
            minDate: new Date(),
        },
    });

    // Popup open buttons
    const jetSkiRentalEmailBtn = document.querySelector(
        ".jet-ski-rental-email-btn"
    );

    // Inquiry form containers as popups
    const rentalInquiry = document.getElementById("jet-ski-rental-inquiry");

    jetSkiRentalEmailBtn.addEventListener("click", function () {
        rentalInquiry.classList.add("form-container--opened");
    });

    rentalInquiry
        .querySelector(".form-close-btn")
        .addEventListener("click", function () {
            rentalInquiry.classList.remove("form-container--opened");
        });

    // Inquiry forms
    const rentalForm = document.getElementById("jet-ski-rental-form");

    // Get the submit buttons
    const rentalSubmitButton = document.getElementById("jet-ski-rental-submit");

    // Create form validators
    let pristinaConfig = {
        errorTextTag: "span",
        errorTextClass: "field-error-message",
    };
    const pristineRental = new Pristine(rentalForm, pristinaConfig, false);

    // Add event listeners to each form field
    rentalForm.addEventListener("input", function () {
        updateSubmitButton(
            rentalForm,
            rentalForm.querySelector('button[type="submit"]')
        );
    });

    // Initially disable submit buttons
    updateSubmitButton(rentalForm, rentalSubmitButton);

    // Add click event listener to the Rental submit button
    rentalSubmitButton.addEventListener("click", function (event) {
        event.preventDefault(); // Prevent default form submission behavior

        // Check if the form is valid
        if (pristineRental.validate()) {
            submitForm("jet-ski-rental-form"); // Call submitForm function with the form ID
        }
    });
});
